import { Link } from "wouter";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { ArrowRight, Calendar, Clock } from "lucide-react";
import type { Event, EventCategory } from "@shared/schema";
import { eventPath } from "@/lib/event-url";

type EventWithCategory = Event & { category?: EventCategory };

interface UpcomingEventsWidgetProps {
  limit?: number;
  categoryId?: string;
}

export function UpcomingEventsWidget({ limit = 5, categoryId }: UpcomingEventsWidgetProps) {
  const queryParams = new URLSearchParams();
  if (categoryId) queryParams.set("categoryId", categoryId);

  const { data: events, isLoading } = useQuery<EventWithCategory[]>({
    queryKey: ["/api/events/upcoming", `?limit=${limit}${categoryId ? `&categoryId=${categoryId}` : ""}`],
  });

  const { data: categories } = useQuery<EventCategory[]>({
    queryKey: ["/api/event-categories"],
  });

  const categoryMap = new Map(categories?.map(c => [c.id, c]) ?? []);

  function formatDate(date: string | Date) {
    return new Date(date).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  }

  function formatTime(date: string | Date) {
    return new Date(date).toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
    });
  }

  return (
    <Card data-testid="widget-upcoming-events">
      <CardHeader className="flex flex-row items-center justify-between gap-4 pb-4">
        <div className="flex items-center gap-2">
          <Calendar className="h-5 w-5 text-primary" />
          <h3 className="font-semibold text-lg">Upcoming Events</h3>
        </div>
        <Link href="/events">
          <Button variant="ghost" size="sm" className="gap-1" data-testid="link-view-all-events">
            View All
            <ArrowRight className="h-3 w-3" />
          </Button>
        </Link>
      </CardHeader>
      <CardContent className="space-y-3">
        {isLoading ? (
          Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="flex items-start gap-3 p-3">
              <Skeleton className="h-3 w-3 rounded-full mt-1 flex-shrink-0" />
              <div className="flex-1 space-y-2">
                <Skeleton className="h-4 w-3/4" />
                <Skeleton className="h-3 w-1/2" />
              </div>
            </div>
          ))
        ) : events && events.length > 0 ? (
          events.map((event) => {
            const cat = event.category || categoryMap.get(event.categoryId);
            return (
              <Link key={event.id} href={eventPath(event)}>
                <div
                  className="flex items-start gap-3 p-3 rounded-md hover-elevate cursor-pointer"
                  data-testid={`widget-event-${event.id}`}
                >
                  <div
                    className="h-3 w-3 rounded-full mt-1.5 flex-shrink-0"
                    style={{ backgroundColor: cat?.color || "#6b7280" }}
                    data-testid={`widget-event-dot-${event.id}`}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-sm truncate" data-testid={`widget-event-title-${event.id}`}>
                      {event.title}
                    </div>
                    <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground flex-wrap">
                      <span className="flex items-center gap-1">
                        <Calendar className="h-3 w-3" />
                        {formatDate(event.startDate)}
                      </span>
                      {!event.allDay && (
                        <span className="flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          {formatTime(event.startDate)}
                        </span>
                      )}
                    </div>
                    {event.description && (
                      <p className="text-xs text-muted-foreground mt-1 line-clamp-1">
                        {event.description}
                      </p>
                    )}
                  </div>
                </div>
              </Link>
            );
          })
        ) : (
          <div className="text-center py-6 text-sm text-muted-foreground" data-testid="widget-no-events">
            No upcoming events scheduled
          </div>
        )}
      </CardContent>
    </Card>
  );
}
